import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Send, AlertCircle, Mail, Clock, ArrowRight } from "lucide-react";
import { PremiumInput } from "../brand/PremiumInput";
import { PremiumTextarea } from "../brand/PremiumTextarea";
import { LIGHT } from "./RoomFlow";
import { api } from "../../lib/api";
import { SkeletonBlock } from "../ui/operational-skeleton";

/* ═══════════════════════════════════════════════════════════════════
   ChaseConfirmDrawer — premium operational confirmation surface
   --------------------------------------------------------------------
   Shown BEFORE a chase is sent. Lets the agent:
     · pick / confirm the recipient (with email)
     · review and edit a context-aware suggested body
     · choose to send a real email (or just log to Flow)
     · cancel cleanly
   Cooldown-aware: refuses to render Send if the backend says so.
   ─────────────────────────────────────────────────────────────────── */

const fmtCooldownLeft = (iso) => {
  if (!iso) return null;
  const ms = Date.parse(iso) - Date.now();
  if (ms <= 0) return null;
  const s = Math.ceil(ms / 1000);
  return `${s}s`;
};

export const ChaseConfirmDrawer = ({ open, dealId, item, onClose, onSent, previewUrl, sendUrl }) => {
  /* Allow callers to override the chase endpoints. v1 callers omit these
     props and get the legacy `/api/deals/{id}/awaiting/chase*` surface;
     v2 callers (Director Centre attention queue) pass explicit URLs that
     resolve against the Postgres-backed deal. */
  const _previewUrl = previewUrl || `/api/deals/${dealId}/awaiting/chase/preview`;
  const _sendUrl = sendUrl || `/api/deals/${dealId}/awaiting/chase`;
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [recipientId, setRecipientId] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [body, setBody] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [cooldownLeft, setCooldownLeft] = useState(null);
  const [error, setError] = useState(null);

  // Load preview when drawer opens
  useEffect(() => {
    if (!open || !item || !dealId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.post(_previewUrl, {
      item_id: item.id,
      title: item.title,
      owner: item.owner,
      days_waiting: typeof item.days === "number" && item.daysSuffix !== "Remaining" ? item.days : 0,
    })
      .then(({ data }) => {
        if (cancelled) return;
        setPreview(data);
        setBody(data.suggested_body || "");
        const first = (data.recipients || [])[0];
        if (first) {
          setRecipientId(first.user_id);
          setRecipientEmail(first.email || "");
        } else {
          setRecipientId("");
          setRecipientEmail("");
        }
        setCooldownLeft(data.cooldown_until ? fmtCooldownLeft(data.cooldown_until) : null);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || "Could not build chase preview");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, item, dealId]);

  // Cooldown ticker (1s)
  useEffect(() => {
    if (!preview?.cooldown_until) return undefined;
    const i = setInterval(() => {
      const left = fmtCooldownLeft(preview.cooldown_until);
      setCooldownLeft(left);
      if (!left) clearInterval(i);
    }, 1000);
    return () => clearInterval(i);
  }, [preview?.cooldown_until]);

  // Esc to close
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const recipients = preview?.recipients || [];
  const selected = recipients.find((r) => r.user_id === recipientId);
  const noRecipient = !selected && !recipientEmail.trim();
  const cooldownActive = !!cooldownLeft;
  const canSend = !sending && !loading && !cooldownActive && body.trim().length > 0;

  const handleSend = async () => {
    setSending(true);
    setError(null);
    try {
      const { data } = await api.post(_sendUrl, {
        item_id: item.id,
        title: item.title,
        owner: item.owner,
        note: body,
        recipient_user_id: recipientId || undefined,
        recipient_email: !recipientId ? (recipientEmail.trim() || undefined) : undefined,
        days_waiting: typeof item.days === "number" && item.daysSuffix !== "Remaining" ? item.days : 0,
        send_email: sendEmail && !noRecipient,
      });
      onSent?.(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.code === "chase_cooldown") {
        setError("This item was just chased — wait a moment before retrying.");
        setCooldownLeft(fmtCooldownLeft(detail.cooldown_until));
      } else {
        setError(typeof detail === "string" ? detail : "Could not send chase");
      }
    } finally {
      setSending(false);
    }
  };

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[80] cf-fade-in"
        onClick={onClose}
        style={{ background: "rgba(5,6,16,0.55)", backdropFilter: "blur(3px)" }}
        data-testid="chase-drawer-backdrop"
      />
      {/* Drawer */}
      <aside
        className="fixed top-0 right-0 z-[81] h-screen w-full sm:w-[480px] flex flex-col cf-slide-in-right"
        style={{
          background: "#FFFFFF",
          borderLeft: `1px solid ${LIGHT.borderStrong}`,
          boxShadow: "-24px 0 48px -16px rgba(15,23,42,0.18)",
        }}
        role="dialog"
        aria-modal="true"
        data-testid="chase-confirm-drawer"
      >
        <header className="flex items-start justify-between gap-3 px-5 sm:px-7 pt-6 pb-4" style={{ borderBottom: `1px solid ${LIGHT.border}` }}>
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.20em]" style={{ color: "#6366F1" }}>Chase</p>
            <h2 className="text-[18px] font-bold mt-1.5 leading-tight truncate" style={{ color: LIGHT.textPrimary }}>
              {item?.title}
            </h2>
            {item?.subtitle && (
              <p className="text-[11.5px] mt-1 truncate" style={{ color: LIGHT.textSubtle }}>
                {item.subtitle}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-md inline-flex items-center justify-center transition-colors hover:bg-slate-100 shrink-0"
            style={{ color: LIGHT.textMuted }}
            aria-label="Close"
            data-testid="chase-drawer-close"
          >
            <X size={14} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 sm:px-7 py-5 space-y-5">
          {loading ? (
            <div className="space-y-5" data-testid="chase-drawer-loading">
              {/* Recipient skeleton — label + 1 card row */}
              <section>
                <SkeletonBlock w="22%" h={9} />
                <div className="mt-1.5 rounded-lg p-3.5" style={{ background: "#FAFAFA", border: `1px solid ${LIGHT.border}` }}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full" style={{ background: "rgba(148,163,184,0.12)" }} />
                    <div className="flex-1 space-y-1.5">
                      <SkeletonBlock w="40%" h={11} />
                      <SkeletonBlock w="65%" h={9} />
                    </div>
                  </div>
                </div>
              </section>
              {/* Context skeleton — label + 3-line padded card */}
              <section>
                <SkeletonBlock w="18%" h={9} />
                <div className="mt-1.5 rounded-lg p-3.5 space-y-2" style={{ background: "#FAFAFA", border: `1px solid ${LIGHT.border}` }}>
                  <SkeletonBlock w="78%" h={9} />
                  <SkeletonBlock w="46%" h={9} />
                </div>
              </section>
              {/* Message skeleton — label + textarea-shaped block (9 rows ≈ 192px) */}
              <section>
                <SkeletonBlock w="20%" h={9} />
                <div className="mt-1.5">
                  <SkeletonBlock w="100%" h={192} />
                </div>
              </section>
            </div>
          ) : (
            <div className="cf-hydrate-fade space-y-5">
              {/* Recipient */}
              <section>
                <Label>Recipient</Label>
                {recipients.length > 0 ? (
                  <div className="space-y-2 mt-1.5">
                    {recipients.map((r) => (
                      <RecipientCard
                        key={r.user_id}
                        recipient={r}
                        active={recipientId === r.user_id}
                        onSelect={() => { setRecipientId(r.user_id); setRecipientEmail(""); }}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="mt-1.5">
                    <PremiumInput
                      type="email"
                      value={recipientEmail}
                      onChange={setRecipientEmail}
                      placeholder={`${item?.owner || "Recipient"} email`}
                      testid="chase-recipient-email"
                    />
                    <p className="text-[10.5px] mt-1.5" style={{ color: LIGHT.textSubtle }}>
                      No participant matched — type the email manually or skip email and just log to Flow.
                    </p>
                  </div>
                )}
              </section>

              {/* Context */}
              <section>
                <Label>Context</Label>
                <div className="mt-1.5 rounded-lg p-3.5"
                  style={{ background: "#FAFAFA", border: `1px solid ${LIGHT.border}` }}
                  data-testid="chase-context"
                >
                  <p className="text-[12px] leading-relaxed" style={{ color: LIGHT.textPrimary }}>
                    <strong>{item?.title}</strong>
                    {item?.days != null && item?.daysSuffix && (
                      <> · <span style={{ color: item.daysSuffix === "Overdue" ? "#B91C1C" : LIGHT.textMuted }}>
                        {item.days} {item.days === 1 ? "day" : "days"} {item.daysSuffix.toLowerCase()}
                      </span></>
                    )}
                  </p>
                  {item?.owner && (
                    <p className="text-[11px] mt-1" style={{ color: LIGHT.textSubtle }}>
                      Awaiting from <span style={{ color: LIGHT.textMuted }}>{item.owner}</span>
                    </p>
                  )}
                </div>
              </section>

              {/* Message preview (editable) */}
              <section>
                <div className="flex items-center justify-between">
                  <Label>Message</Label>
                  <span className="text-[10.5px]" style={{ color: LIGHT.textSubtle }}>
                    Edit before sending
                  </span>
                </div>
                <div className="mt-1.5">
                  <PremiumTextarea
                    value={body}
                    onChange={setBody}
                    rows={9}
                    resize="vertical"
                    placeholder="Write a polite, concise chase…"
                    testid="chase-message"
                  />
                </div>
                <p className="text-[10.5px] mt-1" style={{ color: LIGHT.textSubtle }}>
                  Tip: keep it short and human. Mention exchange impact if relevant.
                </p>
              </section>

              {/* Send-email toggle */}
              <section
                className="rounded-lg p-3.5 flex items-start gap-3"
                style={{ background: "#FAFAFA", border: `1px solid ${LIGHT.border}` }}
              >
                <span className="w-7 h-7 rounded-md inline-flex items-center justify-center shrink-0"
                  style={{ background: sendEmail ? "rgba(99,102,241,0.10)" : "#FFFFFF", color: sendEmail ? "#6366F1" : LIGHT.textMuted, border: `1px solid ${LIGHT.border}` }}
                >
                  <Mail size={11} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[12.5px] font-semibold" style={{ color: LIGHT.textPrimary }}>Also send by email</p>
                  <p className="text-[10.5px] mt-0.5" style={{ color: LIGHT.textSubtle }}>
                    {noRecipient
                      ? "Add a recipient email to enable."
                      : "Sends a premium ChainFlow chase email — you'll still see it in Flow."}
                  </p>
                </div>
                <Toggle
                  checked={sendEmail && !noRecipient}
                  disabled={noRecipient}
                  onChange={(v) => setSendEmail(v)}
                  testid="chase-send-email-toggle"
                />
              </section>

              {/* Cooldown / error */}
              {cooldownActive && (
                <div
                  className="flex items-center gap-2 px-3.5 py-2.5 rounded-md"
                  style={{ background: "#FEF3C7", border: "1px solid #FDE68A", color: "#A16207" }}
                  data-testid="chase-cooldown"
                >
                  <Clock size={11} />
                  <span className="text-[11.5px] font-semibold">
                    Cooldown active — try again in {cooldownLeft}.
                  </span>
                </div>
              )}
              {error && (
                <div
                  className="flex items-center gap-2 px-3.5 py-2.5 rounded-md"
                  style={{ background: "#FEE2E2", border: "1px solid #FECACA", color: "#B91C1C" }}
                  data-testid="chase-error"
                >
                  <AlertCircle size={11} />
                  <span className="text-[11.5px] font-semibold">{error}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <footer
          className="flex items-center gap-2 px-5 sm:px-7 py-4"
          style={{
            borderTop: `1px solid ${LIGHT.border}`,
            background: "#FFFFFF",
            // Clear the Emergent preview badge that sits at bottom-right.
            // Safe-area-aware on mobile.
            paddingBottom: "max(56px, env(safe-area-inset-bottom, 16px))",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-4 rounded-md text-[12.5px] font-semibold transition-colors hover:bg-slate-100"
            style={{ background: "transparent", color: LIGHT.textMuted }}
            data-testid="chase-drawer-cancel"
          >
            Cancel
          </button>
          <div className="flex-1" />
          <button
            type="button"
            onClick={handleSend}
            disabled={!canSend}
            className="h-10 px-4 rounded-md text-white text-[12.5px] font-bold inline-flex items-center gap-1.5 transition-transform hover:-translate-y-0.5 disabled:opacity-50 disabled:translate-y-0 disabled:cursor-not-allowed"
            style={{
              background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)",
              boxShadow: "0 12px 24px -10px rgba(99,102,241,0.55), inset 0 1px 0 rgba(255,255,255,0.16)",
            }}
            data-testid="chase-drawer-send"
          >
            {sending ? "Sending…" : (sendEmail && !noRecipient ? "Send chase" : "Log to Flow")}
            <ArrowRight size={12} />
          </button>
        </footer>
      </aside>
    </>,
    document.body,
  );
};

/* ─── Atoms ─────────────────────────────────────────────────────── */
const Label = ({ children }) => (
  <p className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: LIGHT.textSubtle }}>
    {children}
  </p>
);

const RecipientCard = ({ recipient, active, onSelect }) => {
  const initials = (recipient.full_name || recipient.email || "?")
    .split(/\s+/).slice(0, 2).map((p) => (p ? p[0] : "")).join("").toUpperCase();
  return (
    <button
      type="button"
      onClick={onSelect}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-left"
      style={
        active
          ? {
              background: "linear-gradient(135deg, rgba(139,92,246,0.08), rgba(99,102,241,0.04))",
              border: "1px solid rgba(99,102,241,0.30)",
            }
          : {
              background: "#FFFFFF",
              border: `1px solid ${LIGHT.border}`,
            }
      }
      data-testid={`chase-recipient-${recipient.user_id}`}
    >
      <span
        className="w-9 h-9 rounded-full text-white text-[10.5px] font-bold flex items-center justify-center shrink-0"
        style={{ background: "linear-gradient(135deg, #8B5CF6, #4338CA)" }}
      >
        {initials}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[12.5px] font-semibold truncate" style={{ color: LIGHT.textPrimary }}>
          {recipient.full_name || recipient.email}
        </p>
        <p className="text-[10.5px] truncate" style={{ color: LIGHT.textSubtle }}>
          {recipient.role_label} {recipient.email ? <>· {recipient.email}</> : null}
        </p>
      </div>
      {active && (
        <span
          className="w-4 h-4 rounded-full flex items-center justify-center text-white shrink-0"
          style={{ background: "#6366F1" }}
        >
          <Send size={8} />
        </span>
      )}
    </button>
  );
};

const Toggle = ({ checked, disabled, onChange, testid }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    className="relative w-9 h-5 rounded-full transition-colors disabled:opacity-50"
    style={{ background: checked ? "#6366F1" : "#CBD5E1" }}
    data-testid={testid}
  >
    <span
      className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform"
      style={{ transform: checked ? "translateX(18px)" : "translateX(2px)", boxShadow: "0 1px 2px rgba(15,23,42,0.20)" }}
    />
  </button>
);
